import { type Column, type RowData } from '@tanstack/react-table';
import * as React from 'react';
import { useLayoutEffect } from 'react';

import { cn } from '../utils';

import { DataTableRefContext } from './DataTable.context';
import {
  getPinnedDataAttribute,
  getPinnedEdgeDataAttribute,
  getPinnedSideDataAttribute,
  getStickyCellStyles,
} from './DataTable.utilities';

interface DataTableProps extends React.HTMLAttributes<HTMLTableElement> {
  showScrollbar?: boolean;
}

const DataTable = React.forwardRef<HTMLTableElement, DataTableProps>(
  ({ className, showScrollbar = true, ...props }, ref) => {
    const tableRef = React.useRef<HTMLTableElement | null>(null);

    React.useImperativeHandle(ref, () => tableRef.current as HTMLTableElement);

    return (
      <div
        className={cn('w-full overflow-auto', !showScrollbar && 'no-scrollbar')}
      >
        <DataTableRefContext.Provider value={tableRef}>
          <table
            ref={tableRef}
            className={cn(
              'w-full caption-bottom text-sm border-separate border-spacing-0',
              className,
            )}
            {...props}
          />
        </DataTableRefContext.Provider>
      </div>
    );
  },
);
DataTable.displayName = 'Table';

const DataTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:border-b bg-grey-10 text-xs [&>tr]:first:hover:bg-grey-10 sticky top-0 shadow-sm',
      className,
    )}
    {...props}
  />
));
DataTableHeader.displayName = 'TableHeader';

const DataTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
DataTableBody.displayName = 'TableBody';

const DataTableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    role="rowgroup"
    ref={ref}
    className={cn('sticky bottom-0 z-20 text-xs py-2 px-4', className)}
    {...props}
  />
));
DataTableFooter.displayName = 'TableFooter';

const DataTableRow = React.forwardRef<
  HTMLTableRowElement,
  | (React.HTMLAttributes<HTMLTableRowElement> & {
      highlight?: never;
      onHighlightEnd?: never;
    })
  | (React.HTMLAttributes<HTMLTableRowElement> & {
      highlight: boolean;
      onHighlightEnd: () => void;
    })
>(({ className, onHighlightEnd, highlight, ...props }, ref) => {
  const internalRef = React.useRef<HTMLTableRowElement>(null);

  useLayoutEffect(() => {
    if (highlight) {
      internalRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });

      const timeoutId = setTimeout(() => {
        onHighlightEnd?.();
      }, 6_000);

      return () => clearTimeout(timeoutId);
    }
  }, [highlight]);

  return (
    <tr
      ref={ref || internalRef}
      className={cn(
        'transition-colors hover:bg-grey-5 bg-paper [&>*]:duration-1000',
        // apply pulsating light background to all cells in the row
        highlight &&
          '[&>*]:bg-primary-5 [&>*]:animate-pulse [&>*]:duration-[2000] [&>*]:transition-colors',
        className,
      )}
      {...props}
    />
  );
});
DataTableRow.displayName = 'TableRow';

interface DataTableHeadProps<TData extends RowData, TValue>
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  column?: Column<TData, TValue>;
}

const _DataTableHead = React.forwardRef<
  HTMLTableCellElement,
  DataTableHeadProps<unknown, unknown>
>(({ className, column, style, ...props }, ref) => {
  const stickyHeaderStyles: React.CSSProperties = React.useMemo(
    () => (column ? getStickyCellStyles(column) : {}),
    [column],
  );

  return (
    <th
      ref={ref}
      style={{ ...stickyHeaderStyles, ...style }}
      data-pinned-edge={getPinnedEdgeDataAttribute(column)}
      data-pinned-side={getPinnedSideDataAttribute(column)}
      data-pinned={getPinnedDataAttribute(column)}
      className={cn(
        'h-12 px-4 text-left align-middle font-medium bg-grey-10 [&:has([role=checkbox])]:pr-0',
        'data-[pinned-edge="left"]:shadow-left-col data-[pinned-edge="left"]:z-20 data-[pinned-edge="right"]:shadow-right-col data-[pinned-edge="right"]:z-20 data-[pinned="true"]:z-20',
        className,
      )}
      {...props}
    />
  );
});
_DataTableHead.displayName = 'TableHead';

export const DataTableHead = _DataTableHead as <TData extends RowData, TValue>(
  props: DataTableHeadProps<TData, TValue> &
    React.RefAttributes<HTMLTableCellElement>,
) => React.ReactNode;

interface DataTableCellProps<TData extends RowData, TValue>
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  column?: Column<TData, TValue>;
}

const _DataTableCell = React.forwardRef<
  HTMLTableCellElement,
  DataTableCellProps<unknown, unknown>
>(({ children, className, column, style, ...props }, ref) => {
  const stickyCellStyles: React.CSSProperties = React.useMemo(
    () => (column ? getStickyCellStyles(column) : {}),
    [column],
  );

  return (
    <td
      ref={ref}
      style={{ ...stickyCellStyles, ...style }}
      data-pinned-edge={getPinnedEdgeDataAttribute(column)}
      data-pinned-side={getPinnedSideDataAttribute(column)}
      data-pinned={getPinnedDataAttribute(column)}
      className={cn(
        'py-2 px-4 align-middle bg-inherit border-b border-grey-5',
        'data-[pinned-edge="left"]:shadow-left-col data-[pinned-edge="left"]:z-10 data-[pinned-edge="right"]:shadow-right-col data-[pinned-edge="right"]:z-10 data-[pinned="true"]:z-10',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
});
_DataTableCell.displayName = 'TableCell';

export const DataTableCell = _DataTableCell as <TData extends RowData, TValue>(
  props: DataTableCellProps<TData, TValue> &
    React.RefAttributes<HTMLTableCellElement>,
) => React.ReactNode;

const DataTableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-grey-40', className)}
    {...props}
  />
));
DataTableCaption.displayName = 'TableCaption';

export {
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableFooter,
  DataTableRow,
  DataTableCaption,
};
